import { buildQueryFromArray } from "../../../../utils/buildQuery";
import {
  ApproveExpenseRequest,
  CompleteQBLoginRequest,
  DeleteExpenseRequest,
  DownloadExpenseRequest,
  EditExpenseRequest,
  Expense,
  ExpenseAnalyticsResponse,
  ExpenseResponse,
  GetAuthorizeResponse,
  GetExpenses,
  GetExpensesAnalytics,
  GetQB,
  GetQBCompanyInfoResponse,
  PostExpenseRequest,
  QBSchema
} from "../../../types/expenses";
import {
  ApiResponseMetadata,
  V3APIResponseMetadata
} from "../../../types/shared";
import { baseAPI } from "../../api";

/**
 * EXPENSES ENDPOINTS
 * @author Elvis Rugamba
 * @authorEmail elvisrugamba@awesomity.rw
 * @since Jan 2023
 */

const expensesApi = baseAPI.injectEndpoints({
  endpoints: (builder) => ({
    authorize: builder.query<
      V3APIResponseMetadata<GetAuthorizeResponse>,
      null | void
    >({
      query: () => ({
        providesTags: ["ExpensesAuthorize"],
        url: "/quickbooks-expenses/authorize",
        method: "GET"
      })
    }),
    triggerQBLogin: builder.query<V3APIResponseMetadata<string>, void>({
      query: () => ({
        providesTags: ["TriggerQBLogin"],
        url: "/quickbooks-connect/initiate",
        method: "GET"
      })
    }),
    getQBCompanyInfo: builder.query<
      V3APIResponseMetadata<GetQBCompanyInfoResponse>,
      null | void
    >({
      query: () => ({
        providesTags: ["GetQBCompanyInfo"],
        url: "/quickbooks-connect/company-info",
        method: "GET"
      })
    }),
    completeQBLogin: builder.query<
      V3APIResponseMetadata<void>,
      CompleteQBLoginRequest
    >({
      query: (params) => ({
        params,
        providesTags: ["CompleteQBLogin"],
        url: "/quickbooks-connect/callback",
        method: "GET"
      })
    }),
    expenses: builder.query<
      ApiResponseMetadata<{ content: ExpenseResponse }>,
      GetExpenses
    >({
      providesTags: ["Expenses"],
      query: ({ page, size = 500, sort, depotId, signal }) => ({
        url: `/quickbooks-expenses?page=${page || "0"}&size=${
          size || ""
        }&sort=${sort || ""}&depot=${depotId || ""}`,
        method: "GET",
        signal: signal
      })
    }),
    postExpense: builder.mutation<
      ApiResponseMetadata<Expense>,
      PostExpenseRequest
    >({
      query: ({ formData }) => ({
        url: `/quickbooks-expenses`,
        method: "POST",
        body: formData
      })
    }),
    deleteExpense: builder.mutation<
      ApiResponseMetadata<Expense>,
      DeleteExpenseRequest
    >({
      query: ({ ids }) => ({
        url: `/quickbooks-expenses?${buildQueryFromArray("ids", ids)}`,
        method: "DELETE"
      })
    }),
    editExpense: builder.mutation<
      ApiResponseMetadata<Expense>,
      EditExpenseRequest
    >({
      query: ({ id, formData }) => ({
        url: `/quickbooks-expenses/${id || ""}`,
        method: "PATCH",
        body: formData
      })
    }),
    suppliers: builder.query<ApiResponseMetadata<{ content: QBSchema }>, GetQB>(
      {
        providesTags: ["QBSuppliers"],
        query: ({ search }) => ({
          url: `/quickbooks-expenses/vendors?search=${search || ""}`,
          method: "GET"
        })
      }
    ),
    trucks: builder.query<ApiResponseMetadata<{ content: QBSchema }>, GetQB>({
      providesTags: ["QBTrucks"],
      query: ({ search }) => ({
        url: `/quickbooks-expenses/classes?search=${search || ""}`,
        method: "GET"
      })
    }),
    locations: builder.query<ApiResponseMetadata<{ content: QBSchema }>, GetQB>(
      {
        providesTags: ["QBLocations"],
        query: ({ search }) => ({
          url: `/quickbooks-expenses/locations?search=${search || ""}`,
          method: "GET"
        })
      }
    ),
    paymentMethods: builder.query<
      ApiResponseMetadata<{ content: QBSchema }>,
      GetQB
    >({
      providesTags: ["QBPaymentMethods"],
      query: ({ search }) => ({
        url: `/quickbooks-expenses/payment-methods?search=${search || ""}`,
        method: "GET"
      })
    }),
    accounts: builder.query<ApiResponseMetadata<{ content: QBSchema }>, GetQB>({
      providesTags: ["QBAccounts"],
      query: ({ search }) => ({
        url: `/quickbooks-expenses/accounts?search=${search || ""}`,
        method: "GET"
      })
    }),

    qbCategories: builder.query<
      { payload: QBSchema[]; message: string; statusCode: number },
      GetQB
    >({
      providesTags: ["QBCategories"],
      query: ({ search }) => ({
        url: `/quickbooks-expenses/categories?search=${search || ""}`,
        method: "GET"
      })
    }),

    approve: builder.mutation<
      ApiResponseMetadata<Expense>,
      ApproveExpenseRequest
    >({
      invalidatesTags: ["Expenses"],
      query: (DTO) => ({
        url: "/quickbooks-expenses/approve",
        method: "PATCH",
        body: DTO
      })
    }),
    downloadFromServer: builder.query<File, DownloadExpenseRequest>({
      query: ({ id }) => ({
        url: `quickbooks-expenses/${id}/attachable/server/download`,
        method: "GET",
        headers: {
          "content-type": "application/octet-stream"
        },
        responseHandler: (response) => response.blob()
      })
    }),
    downloadFromQB: builder.query<
      ApiResponseMetadata<string>,
      DownloadExpenseRequest
    >({
      query: ({ id }) => ({
        url: `quickbooks-expenses/${id}/attachable/qb/download`,
        method: "GET"
      })
    }),

    downloadExpenseTracker: builder.query({
      query: ({
        depot = "",
        start = "",
        end = "",
        file_type = "CSV",
        page = "",
        qbTruckId = ""
      }) => ({
        url: `/quickbooks-expenses/download-report?depot=${depot || ""}&start=${
          start || ""
        }&end=${
          end || ""
        }&file_type=${file_type}&page=${page}&size=1000&qbTruckId=${qbTruckId}`,
        responseHandler: (response) => response.blob()
      })
    }),

    expensesAnalytics: builder.query<
      { payload: ExpenseAnalyticsResponse; message: string },
      GetExpensesAnalytics
    >({
      query: ({ depot, start = "", end = "", qbCategoryIds }) => ({
        url: `/quickbooks-expenses/expenses-analytics?depot=${
          depot || ""
        }&start=${start || ""}&end=${end || ""}&${buildQueryFromArray(
          "qbCategoryIds",
          qbCategoryIds
        )}`,
        method: "GET"
      })
    })
  })
});

export const {
  useLazyAuthorizeQuery,
  useAuthorizeQuery,
  useLazyExpensesQuery,
  usePostExpenseMutation,
  useDeleteExpenseMutation,
  useEditExpenseMutation,
  useLazySuppliersQuery,
  useLazyTrucksQuery,
  useLazyLocationsQuery,
  useLazyPaymentMethodsQuery,
  useLazyAccountsQuery,
  useApproveMutation,
  useLazyDownloadFromServerQuery,
  useLazyDownloadFromQBQuery,
  useLazyQbCategoriesQuery,
  useQbCategoriesQuery,
  useLazyDownloadExpenseTrackerQuery,
  useLazyExpensesAnalyticsQuery,
  useExpensesAnalyticsQuery,
  useLazyTriggerQBLoginQuery,
  useGetQBCompanyInfoQuery,
  useLazyCompleteQBLoginQuery
} = expensesApi;
